import {
  AutoComplete,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { debounce } from 'lodash';
import { User } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2 } from 'react-icons/bs';
import { CiLocationOn } from 'react-icons/ci';

import useStore from '../../../store/useStore';
import { formatCurrency, getBaseSku } from '../../../utils/format';
import { getContractTypeOptions, hasStockSource } from '../utils/utils';
import { AddOrderDeliveryContext } from './AddOrderDeliveryContext';
import Branch from './Branch';
import SectionCard from '../../../components/sectionCard/SectionCard';
import StepFooter from '../../../components/stepFooter/StepFooter';
import Warehouse from './Warehouse';

const { confirm } = Modal;
const { Text } = Typography;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

const SOLVED_TYPE = [
  { value: 'B2Bprice', label: 'B2B' },
  { value: 'B2Cprice', label: 'B2C' },
];

const Step1 = ({ nextStep }) => {
  const [form] = Form.useForm();
  const isB2B = useStore((state) => state?.user?.isB2B);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState();
  const [customersLoading, setCustomersLoading] = useState(false);
  // Гэрээний мэдээлэл ирэхээс өмнө "төрлөө сонгоно уу" гэсэн хоосон карт харагдахгүй байхаар
  const [referenceLoading, setReferenceLoading] = useState(true);

  const {
    fromBranch,
    setFromBranch,
    warehouseList,
    setWareHouseList,
    clearSelectedWareHouses,
    addWarehouse,
    addBranch,
    branchList,
    setBranchList,
    fromWarehouse,
    setFromWareHouse,
    reference,
    setReference,
    formDataStep1,
    setFormDataStep1,
    setSelectedCustomerField,
    selectedCustomerField,
    clearSelectedBranches,
    /**
     * Эдгээр нь контекстэд байна — Step1 нь алхам солиход unmount болдог тул
     * локал state байвал "Буцах" дарахад эсвэл refresh хийхэд сонголт нь
     * (нөхцөл, гэрээний төрөл) алга болж, бараа сонгох блокууд харагдахаа
     * больдог байсан.
     */
    solvedPrice,
    setSolvedPrice,
    usedContractType,
    setUsedContractType,
    warehouseCode,
    setWarehouseCode,
  } = useContext(AddOrderDeliveryContext) ?? {};

  // Step1 руу буцаж ирэхэд өмнө сонгосон харилцагчийн нэр input-д харагдана
  const [customerInput, setCustomerInput] = useState(selectedCustomerField?.name ?? '');

  const contractType = reference?.contract?.data?.contractType;
  const availableServiceTypes = reference?.contract?.contractServiceTypes;
  const availableServices = reference?.contract?.data?.availableService;
  const enumDictionary = reference?.contract?.enumDictionary;

  const getCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const { data } = await axios.post('/api/v1.0/merchant-customer/search', {
        phoneNumber: '',
        name: '',
      });
      setCustomers(data.result.docs || []);
    } catch (e) {
      console.error('getCustomers error', e);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  useEffect(() => {
    getCustomers();
    axios
      .get('/api/v1/request/delivery-reference')
      .then(({ data }) => {
        if (data.statusCode !== 200) return;

        setReference(data?.result);

        const contractTypes = data.result?.contract?.data?.contractType ?? [];
        if (contractTypes.length === 1) setUsedContractType(contractTypes[0]);

        // Нэг л үйлчилгээний төрөл байвал нөхцөлийг гараар сонгуулахгүй
        const serviceTypes = [
          ...new Set(
            (data?.result?.contract?.data?.availableService ?? []).map((item) => item.type)
          ),
        ];
        // Хэрэглэгчийн сонголтыг (refresh хийхэд draft-аас сэргэсэн) дарж бичихгүй
        if (serviceTypes.length <= 1 && !solvedPrice) {
          setSolvedPrice(isB2B?.[0] === 'B2C' ? 'B2Cprice' : 'B2Bprice');
        }
      })
      .catch((err) => {
        console.error('delivery-reference error: ', err);
      })
      .finally(() => setReferenceLoading(false));
    //eslint-disable-next-line
  }, []);

  /**
   * Сонгосон гэрээний төрөлд харгалзах нөхцөлүүд. State болгож хадгалалгүй
   * гаргаж авснаар refresh хийхэд/буцаж ирэхэд дахин тооцоологдоно.
   */
  const possbileServiceTypes = useMemo(() => {
    if (!usedContractType) return [];

    const match = (availableServiceTypes ?? []).find((service) =>
      service?.contractType?.includes(usedContractType)
    );
    return match?.types ?? [];
  }, [availableServiceTypes, usedContractType]);

  /**
   * On-demand (салбараас татах) үйлчилгээнд бараа хүлээн авах агуулахууд.
   * Хуучин логикийн дагуу зөвхөн олон гэрээний төрөлтэй мерчант "Хүргэлтийн төрөл"-өөс
   * 'O'-г сонгоход л харуулна. Ганц 'O' гэрээтэй мерчантад агуулах сонгуулахгүй — эс
   * тэгвээс warehouseCode илгээгдэж, салбарын барааны байршил агуулахаар солигддог.
   */
  const ondemandWarehouses = useMemo(() => {
    if (usedContractType !== 'O' || !(contractType?.length > 1)) return { types: [], detail: {} };

    return {
      types: reference?.warehouseOndemand?.type ?? [],
      detail: reference?.warehouseOndemand?.detail ?? {},
    };
  }, [usedContractType, contractType, reference?.warehouseOndemand]);

  const mTotalPrice = useMemo(() => {
    const items = [...warehouseList, ...branchList];
    return items.reduce(
      (sum, item) =>
        sum + (item.products ?? []).reduce((acc, p) => acc + p?.totalUnit * p?.price, 0),
      0
    );
  }, [warehouseList, branchList]);

  const onChangeSelectWareHouse = (checked) => {
    if (!checked) {
      confirm({
        title: 'Агуулахаас татахаар сонгосон бараануудыг устгах уу?',
        okText: 'Тийм',
        cancelText: 'Үгүй',
        onOk: () => {
          setFromWareHouse(false);
          setWareHouseList([]);
          clearSelectedWareHouses();
        },
      });
      return;
    }
    if (warehouseList.length === 0) addWarehouse();
    setFromWareHouse(checked);
  };

  const onChangeSelectBranch = (checked) => {
    if (!checked) {
      confirm({
        title: 'Салбараас татахаар сонгосон бараануудыг устгах уу?',
        okText: 'Тийм',
        cancelText: 'Үгүй',
        onOk: () => {
          setFromBranch(false);
          setBranchList([]);
          clearSelectedBranches();
        },
      });
      return;
    }
    if (branchList.length === 0) addBranch();
    setFromBranch(checked);
  };

  /**
   * Хэрэглэгч гэрээнд байгаа хоёр эх сурвалжаас (агуулах/салбар) заримыг л
   * хэрэглэсэн бол гэрээний төрлийг нарийсгана: WO → зөвхөн агуулахаас бол 'W',
   * зөвхөн салбараас бол 'O'. Өмнө нь `reference`-ыг өөрчилдөг байсан тул
   * Step1 рүү буцаж ирэхэд сонголтын жагсаалт эвдэрдэг байсан — одоо зөвхөн
   * `formDataStep1.contractType` дээр буулгана.
   */
  const resolveContractType = () => {
    if (usedContractType !== 'WO') return usedContractType;

    const hasWarehouseProducts =
      fromWarehouse && warehouseList.some((item) => item.products?.length);
    const hasBranchProducts = fromBranch && branchList.some((item) => item.products?.length);

    if (hasWarehouseProducts && hasBranchProducts) return 'WO';
    if (hasWarehouseProducts) return 'W';
    if (hasBranchProducts) return 'O';
    return usedContractType;
  };

  const handleCalcPrice = (type) => {
    setSolvedPrice(type);
    if (type === 'B2Cprice') {
      setSelectedCustomer('');
      setCustomerInput('');
      // B2C-д харилцагч хамаарахгүй — Step2 дээр нэр/утас нь блоклогдохгүй
      setSelectedCustomerField(undefined);
    }
  };

  const searchCustomers = useMemo(
    () =>
      debounce(async (searchValue) => {
        if (!searchValue || searchValue.length === 0) {
          getCustomers();
          return;
        }
        setCustomersLoading(true);
        try {
          const isPhone = /^\d+$/.test(searchValue.trim());
          const { data } = await axios.post('/api/v1.0/merchant-customer/search', {
            phoneNumber: isPhone ? searchValue.trim() : '',
            name: isPhone ? '' : searchValue.trim(),
          });
          setCustomers(data.statusCode === 200 ? (data.result?.docs ?? []) : []);
        } catch (e) {
          console.error('searchCustomers error', e);
        } finally {
          setCustomersLoading(false);
        }
      }, 1000),
    [getCustomers]
  );

  /**
   * Бичсэн текст өөрчлөгдвөл өмнө сонгосон харилцагч таарахгүй болох тул
   * сонголтыг цуцална. Сонгох үед AutoComplete эхлээд `onChange`-ийг `_id`-тай
   * дуудаж, дараа нь `onSelect` дуудагддаг тул сүүлийнх нь ялна.
   */
  const handleCustomerInputChange = (value) => {
    setCustomerInput(value);
    setSelectedCustomer(undefined);
    setSelectedCustomerField(undefined);
  };

  const handleChange = (value) => {
    const customer = customers.find((obj) => obj._id === value);
    setSelectedCustomer(value);
    setSelectedCustomerField(customer);
    setCustomerInput(customer?.name ?? '');
  };

  const handleChangeContractType = (handleContractType) => {
    /**
     * Нэг гэрээний төрөл олон агуулахтай байж болно (жишээ: W → AWH, CWH).
     * Өмнө нь `find`-аар зөвхөн эхнийг нь шалгадаг тул хоёр дахь агуулахад нь
     * үлдэгдэлтэй бараа байсан ч хуурамч алдаа өгдөг байсан — одоо бүгдийг шалгана.
     */
    const isKnownWarehouse = (availableServices ?? [])
      .filter((item) => item.contractType === handleContractType)
      .some(
        (item) =>
          reference?.warehouse?.type?.includes(item.warehouseCode) ||
          reference?.warehouseOndemand?.type?.includes(item.warehouseCode)
      );

    if (!isKnownWarehouse) {
      message.error(
        'Гэрээнд заасан агуулахад үлдэгдэлтэй бараа байхгүй байна. Эхлээд татан авалт хийж бараагаа байршуулна уу.'
      );
      return;
    }

    setUsedContractType(handleContractType);
  };

  const onFinish = () => {
    if (!usedContractType) {
      message.error('Хүргэлтийн төрлөө сонгоно уу!');
      return;
    }

    if (mTotalPrice === 0) {
      message.error('Бараа сонгоогүй байна!');
      return;
    }

    const products = [];
    [...warehouseList, ...branchList].forEach((item) =>
      item.products.forEach((p) => {
        const obj = {
          baseSku: getBaseSku(p.productSku),
          productSKU: p.productSku,
          productTitle: p.title,
          productImage: p.imageCover,
          productPrice: p.price,
          productTotalUnit: p.totalUnit,
          productTotalWeight: p.weight * p.totalUnit,
          productLocation: p.productLocation,
          productLocationCode: p.productLocationCode,
          productGroup: p.isGroup,
          productGroups: p.group || null,
          productDescription: p.description,
        };
        if (p.productPositionCode) obj.productPositionCode = p.productPositionCode;
        if (p.batchCode) obj.batchCode = p.batchCode;
        if (p.expiryDate) obj.expiryDate = p.expiryDate;
        products.push(obj);
      })
    );

    const step1Values = {
      products,
      formValues: form.getFieldsValue(),
      solvedPrice,
      warehouseCode:
        warehouseCode ||
        warehouseList.find((w) => w.products.length > 0)?.products[0]?.productLocationCode,
      contractType: resolveContractType(),
    };
    if (selectedCustomerField) step1Values.customerId = selectedCustomerField._id;

    setFormDataStep1(step1Values);
    nextStep();
  };

  const customerOptions = customers.map((customer) => ({
    value: customer?._id,
    label2: customer?.name,
    label: (
      <Row gutter={8} style={{ display: 'flex', alignItems: 'center' }}>
        <Col>
          <User size={18} style={{ color: '#758195' }} />
        </Col>
        <Col>
          <div style={{ maxWidth: 200, whiteSpace: 'normal' }}>{customer?.name || '-'}</div>
          <div style={{ fontSize: 12 }}>{customer?.phoneNumber}</div>
          <div style={{ fontSize: 12 }}>
            {customer?.address?.[0]?.name || '-'} {customer?.address?.[1]?.name || '-'}{' '}
            {customer?.address?.[2]?.name || '-'}
          </div>
        </Col>
      </Row>
    ),
  }));

  /**
   * Энэ хуудсаар үйлчлэх боломжтой төрлүүд л сонголтод гарна — `utils.js`-ийг үз.
   * Шууд хүргэлт (D) нь тусдаа хуудастай.
   */
  const contractTypeOptions = getContractTypeOptions(enumDictionary, contractType);

  /**
   * Карт дотор харагдах 3 талбар бүгд нуугдах тохиолдолд (гэрээний төрөл нэг,
   * нөхцөл нэг, B2B биш) зөвхөн гарчигтай хоосон карт үлддэг тул бүтнээрээ нуана.
   */
  const showPriceConditionCard =
    contractType?.length > 1 ||
    possbileServiceTypes.length > 1 ||
    (isB2B?.includes('B2B') && solvedPrice === 'B2Bprice');

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ fromWarehouse: true, ...(formDataStep1?.formValues ?? {}) }}
      /**
       * Refresh хийхэд сонгосон агуулах, тоо ширхэг сэргэхийн тулд формын утгыг
       * контекст рүү шингээнэ (контекст нь sessionStorage-д хадгална). Энэ алхамд
       * зөвхөн select/тоо байдаг тул гар бичихээс үүдэх дахин рендер байхгүй.
       */
      onValuesChange={(_, allValues) =>
        setFormDataStep1((prev) => ({ ...prev, formValues: allValues }))
      }
    >
      <Spin spinning={referenceLoading}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {showPriceConditionCard && (
            <SectionCard icon={<AiOutlineInfoCircle />} title="Төлбөр бодох нөхцөл">
              <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
                {contractType?.length > 1 && (
                  <Col xs={24} sm={12} xl={8}>
                    <Form.Item label="Хүргэлтийн төрөл" required style={{ marginBottom: 0 }}>
                      {/* `value` — refresh хийхэд/буцаж ирэхэд сонголт нь харагдана */}
                      <Select
                        placeholder="Сонгох"
                        value={usedContractType}
                        onChange={handleChangeContractType}
                        options={contractTypeOptions}
                      />
                    </Form.Item>
                  </Col>
                )}

                {possbileServiceTypes.length > 1 && (
                  <Col xs={24} sm={12} xl={8}>
                    <Form.Item label="Нөхцөл" required style={{ marginBottom: 0 }}>
                      <Select
                        value={solvedPrice}
                        onChange={handleCalcPrice}
                        options={SOLVED_TYPE}
                      />
                    </Form.Item>
                  </Col>
                )}

                {isB2B?.includes('B2B') && solvedPrice === 'B2Bprice' && (
                  <Col xs={24} sm={12} xl={8}>
                    <Form.Item label="Харилцагчийн мэдээлэл" style={{ marginBottom: 0 }}>
                      <Tooltip title="Утас эсвэл нэрээр хайх" placement="right">
                        <AutoComplete
                          value={customerInput}
                          onChange={handleCustomerInputChange}
                          onSelect={handleChange}
                          onSearch={searchCustomers}
                          options={customerOptions}
                          /* Хайлт нь серверт хийгддэг тул локалаар дахин шүүхгүй */
                          filterOption={false}
                          notFoundContent={customersLoading ? <Spin size="small" /> : null}
                        >
                          <Input placeholder="Утас эсвэл нэр" allowClear />
                        </AutoComplete>
                      </Tooltip>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </SectionCard>
          )}

          {/**
           * Доорх агуулах/салбарын аль нь ч гарахгүй бүх тохиолдолд ЯМАГТ энэ
           * тайлбар харагдана. Өмнө нь зөвхөн "төрөл сонгоогүй" үед харагддаг
           * байсан тул бараа сонгох эх сурвалжгүй төрөл сонгоход хуудас хоосон
           * үлдээд, "Үргэлжлүүлэх" нь бараа шаардаж гацаадаг байв.
           */}
          {!hasStockSource(usedContractType) && (
            <SectionCard icon={<BsBox2 />} title="Бараа">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary">
                    {usedContractType
                      ? 'Энэ хүргэлтийн төрлөөр агуулах, салбараас бараа сонгодоггүй. Захиалгын жагсаалтаас «Шинэ захиалга → Шууд хүргэлт»-ийг сонгоно уу.'
                      : 'Бараа сонгохын тулд эхлээд хүргэлтийн төрлөө сонгоно уу.'}
                  </Text>
                }
                style={{ margin: 0, padding: '24px 0' }}
              />
            </SectionCard>
          )}

          {/* W: Агуулах дээр бараа байршуулах үйлчилгээ */}
          {usedContractType?.includes('W') && (
            <SectionCard
              icon={<BsBox2 />}
              title="Агуулахаас бараа сонгох"
              action={
                <Switch
                  name="fromWarehouse"
                  checked={fromWarehouse}
                  onChange={onChangeSelectWareHouse}
                />
              }
              styles={{ body: { padding: fromWarehouse ? undefined : 0 } }}
            >
              {fromWarehouse &&
                warehouseList.map((warehouse) => (
                  <Warehouse
                    key={warehouse.id}
                    id={warehouse.id}
                    products={warehouse.products}
                    form={form}
                    setWarehouse={setWarehouseCode}
                    selectedCustomer={selectedCustomer}
                  />
                ))}
            </SectionCard>
          )}

          {/* O: On-demand татан авалттай B2C үйлчилгээ */}
          {usedContractType?.includes('O') && (
            <SectionCard
              icon={<CiLocationOn />}
              title="Салбараас бараа сонгох"
              action={
                <Switch name="fromBranch" checked={fromBranch} onChange={onChangeSelectBranch} />
              }
              styles={{ body: { padding: fromBranch ? undefined : 0 } }}
            >
              {fromBranch &&
                branchList.map((item) => (
                  <Branch
                    key={item.id}
                    id={item.id}
                    products={item.products}
                    form={form}
                    setWarehouse={setWarehouseCode}
                    selectedCustomer={selectedCustomer}
                    warehouses={ondemandWarehouses}
                  />
                ))}
            </SectionCard>
          )}

          <StepFooter
            extra={
              mTotalPrice > 0 ? (
                <Text type="secondary">
                  Барааны нийт дүн: <Text strong>{formatCurrency(mTotalPrice)}</Text>
                </Text>
              ) : null
            }
          />
        </Space>
      </Spin>
    </Form>
  );
};

export default Step1;
